import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    Alert,
    Box,
    TablePagination,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetTransactions } from '@openapi/transactions/transactions';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { useModals, MODAL_TYPE } from '../../../hooks/useModal';

function ManualRefundsTable({ filters, onFiltersChange }) {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const [refreshKey, setRefreshKey] = useState(0);

    // Build query parameters for cash transactions from refunding orders
    const queryParams = {
        search: filters.search || undefined,
        page: filters.page,
        pageSize: filters.pageSize,
        sort: filters.sort,
        sortDir: filters.sortDir,
        finished: filters.finished || undefined,
        paymentType: 'cash',
        orderStatus: 'refunding',
    };

    const {
        data: transactionsData,
        isLoading,
        error: isError,
        refetch,
    } = useGetTransactions(queryParams, {
        query: {
            enabled: true,
        },
    });

    // Trigger refetch when refreshKey changes
    useEffect(() => {
        if (refreshKey > 0) {
            refetch();
        }
    }, [refreshKey, refetch]);

    const transactions = transactionsData?.transactions || [];

    const handleSort = (column) => {
        const isAsc = filters.sort === column && filters.sortDir === 'ASC';
        onFiltersChange({
            ...filters,
            sort: column,
            sortDir: isAsc ? 'DESC' : 'ASC',
            page: 0,
        });
    };

    const handleRefund = (transaction) => {
        showModal(MODAL_TYPE.MANUAL_REFUND, {
            transaction,
            onSuccess: () => {
                setRefreshKey((prev) => prev + 1);
            },
        });
    };

    const handlePageChange = (event, newPage) => {
        onFiltersChange({
            ...filters,
            page: newPage,
        });
    };

    const handleRowsPerPageChange = (event) => {
        onFiltersChange({
            ...filters,
            pageSize: parseInt(event.target.value, 10),
            page: 0,
        });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return <Alert severity="error">{t('common.error_loading_data')}</Alert>;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell
                                arrow={filters.sort === 'id' ? filters.sortDir : ''}
                                onClick={() => handleSort('id')}
                            >
                                {t('transactions.columns.id')}
                            </SortableTableCell>
                            <TableCell>{t('transactions.columns.showName')}</TableCell>
                            <TableCell>{t('transactions.columns.startTime')}</TableCell>
                            <TableCell>{t('transactions.columns.booker')}</TableCell>
                            <SortableTableCell
                                arrow={filters.sort === 'createdAt' ? filters.sortDir : ''}
                                onClick={() => handleSort('createdAt')}
                            >
                                {t('transactions.columns.createdAt')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={filters.sort === 'amount' ? filters.sortDir : ''}
                                onClick={() => handleSort('amount')}
                            >
                                {t('transactions.columns.amount')}
                            </SortableTableCell>
                            <TableCell>{t('transactions.columns.payment_type')}</TableCell>
                            <TableCell>{t('transactions.columns.refund_note')}</TableCell>
                            <TableCell>{t('transactions.columns.finishedBy')}</TableCell>
                            <TableCell>{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        sx={{ py: 4 }}
                                    >
                                        {t('manualrefunds.no_transactions')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((transaction) => (
                                <TableRow key={transaction.id}>
                                    <TableCell>{transaction.id}</TableCell>
                                    <TableCell>{transaction.showTitle}</TableCell>
                                    <TableCell>
                                        {formatDateTimeInVenueTz(transaction.startTime)}
                                    </TableCell>
                                    <TableCell>{transaction.bookerName}</TableCell>
                                    <TableCell>
                                        {formatDateTimeInVenueTz(transaction.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        {formatCentsToUsd(-1 * transaction.amount)}
                                    </TableCell>
                                    <TableCell>{transaction.paymentType}</TableCell>
                                    <TableCell>{transaction.refundNote}</TableCell>
                                    <TableCell>
                                        <small>
                                            {transaction.finishedByUser && (
                                                <>
                                                    {transaction.finishedByUser}
                                                    <br />
                                                </>
                                            )}
                                            {formatDateTimeInVenueTz(transaction.finishedAt)}
                                        </small>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            disabled={transaction.finished}
                                            onClick={() => handleRefund(transaction)}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {transaction.finished
                                                ? t('manualrefunds.already_refunded')
                                                : t('manualrefunds.do_refund')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={transactionsData.total}
                page={filters.page}
                onPageChange={handlePageChange}
                rowsPerPage={filters.pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />
        </Box>
    );
}

export default ManualRefundsTable;
